import { useEffect, useState } from 'react';
import { api, ApiError } from '../api';
import { formatRelative } from '../format';
import type { AiSummaryRecord, LeadAiSummary, LeadSummarySources, LocationAiSummary } from '../types';

function message(cause: unknown): string {
  return cause instanceof ApiError ? cause.message : String(cause);
}

function List({ title, items, tone }: { title: string; items: string[]; tone?: 'good' | 'bad' | 'act' }) {
  if (!items.length) return null;
  return (
    <div className={`ai-list${tone ? ` ai-${tone}` : ''}`}>
      <div className="ai-list-title">{title}</div>
      <ul>
        {items.map((item, index) => (
          <li key={index}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

function Footer({ record, extra }: { record: AiSummaryRecord<unknown>; extra?: string }) {
  return (
    <div className="ai-footer">
      Updated {formatRelative(record.createdAt)}
      {extra ? ` · ${extra}` : ''} · AI can make mistakes.
    </div>
  );
}

function NotReady({ what }: { what: string }) {
  return (
    <div className="ai-panel ai-empty">
      <span className="ai-spark" aria-hidden="true">✦</span>
      <span>
        AI {what} are not set up yet — add an AI key under <strong>Settings</strong>.
      </span>
    </div>
  );
}

function describeSources(sources: LeadSummarySources | null): string {
  if (!sources) return '';
  const parts = [
    sources.messages ? `${sources.messages} messages` : null,
    sources.notes ? `${sources.notes} notes` : null,
    sources.transcripts ? `${sources.transcripts} transcripts` : null,
  ].filter(Boolean);
  return parts.length ? `read ${parts.join(', ')}` : '';
}

const TEMPERATURE_LABEL: Record<LeadAiSummary['temperature'], string> = {
  hot: 'Hot',
  warm: 'Warm',
  cold: 'Cold',
  unknown: 'Unclear',
};

/** The AI read on one lead, at the top of their journey. */
export function LeadSummaryPanel({ contactId }: { contactId: string }) {
  const [record, setRecord] = useState<AiSummaryRecord<LeadAiSummary> | null>(null);
  const [ready, setReady] = useState<boolean | null>(null);
  const [newActivity, setNewActivity] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .leadSummary(contactId)
      .then((response) => {
        if (cancelled) return;
        setRecord(response.summary);
        setReady(response.ready);
        setNewActivity(response.newActivity);
      })
      .catch((cause) => !cancelled && setError(message(cause)));
    return () => {
      cancelled = true;
    };
  }, [contactId]);

  async function generate() {
    setBusy(true);
    setError(null);
    try {
      const response = await api.generateLeadSummary(contactId);
      setRecord(response.summary);
      setNewActivity(false);
    } catch (cause) {
      setError(message(cause));
    } finally {
      setBusy(false);
    }
  }

  if (ready === null && !error) return null;
  if (ready === false && !record) return <NotReady what="lead summaries" />;

  const sources = (record?.sources ?? null) as LeadSummarySources | null;
  const content = record?.content;

  return (
    <section className={`ai-panel${busy ? ' reloading' : ''}`} aria-label="AI lead summary">
      <div className="ai-head">
        <span className="ai-title">
          <span className="ai-spark" aria-hidden="true">✦</span> AI summary
          {content ? <span className={`temp temp-${content.temperature}`}>{TEMPERATURE_LABEL[content.temperature]}</span> : null}
          {content?.stage ? <span className="ai-stage">{content.stage}</span> : null}
        </span>
        <button type="button" className="btn btn-sm" disabled={busy || ready === false} onClick={generate}>
          {busy ? 'Reading the history…' : record ? 'Refresh' : 'Summarise this lead'}
        </button>
      </div>
      {error ? <div className="inline-result bad">{error}</div> : null}
      {newActivity && record ? (
        <div className="inline-result">New activity since this summary was written — refresh to include it.</div>
      ) : null}
      {content ? (
        <>
          <div className="ai-headline">{content.headline}</div>
          <p className="ai-text">{content.summary}</p>
          <div className="ai-columns">
            <List title="Key facts" items={content.keyFacts} />
            <List title="Concerns" items={content.concerns} tone="bad" />
            <List title="Next steps" items={content.nextSteps} tone="act" />
          </div>
          {sources?.unavailable?.length ? (
            <div className="cell-sub">Not available: {sources.unavailable.map((item) => item.split(' (')[0]).join(', ')}.</div>
          ) : null}
          <Footer record={record!} extra={describeSources(sources)} />
        </>
      ) : !busy ? (
        <p className="small muted" style={{ margin: 0 }}>
          Reads the conversation, notes, calls, appointments and pipeline history, and says where this lead stands and
          what to do next.
        </p>
      ) : null}
    </section>
  );
}

/** The AI read on a sub-account over a period. */
export function LocationSummaryPanel({ locationId, from, to }: { locationId: string; from: string; to: string }) {
  const [record, setRecord] = useState<AiSummaryRecord<LocationAiSummary> | null>(null);
  const [ready, setReady] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setRecord(null);
    setError(null);
    api
      .locationSummary(locationId, from, to)
      .then((response) => {
        if (cancelled) return;
        setRecord(response.summary);
        setReady(response.ready);
      })
      .catch((cause) => !cancelled && setError(message(cause)));
    return () => {
      cancelled = true;
    };
  }, [locationId, from, to]);

  async function generate() {
    setBusy(true);
    setError(null);
    try {
      setRecord((await api.generateLocationSummary(locationId, from, to)).summary);
    } catch (cause) {
      setError(message(cause));
    } finally {
      setBusy(false);
    }
  }

  if (ready === null && !error) return <p className="small muted">Loading…</p>;
  if (ready === false && !record) return <NotReady what="sub-account summaries" />;
  const content = record?.content;

  return (
    <section className={`ai-panel${busy ? ' reloading' : ''}`} aria-label="AI sub-account summary">
      <div className="ai-head">
        <span className="ai-title">
          <span className="ai-spark" aria-hidden="true">✦</span> AI summary · {from} to {to}
        </span>
        <button type="button" className="btn btn-sm" disabled={busy || ready === false} onClick={generate}>
          {busy ? 'Analysing…' : record ? 'Regenerate' : 'Summarise this period'}
        </button>
      </div>
      {error ? <div className="inline-result bad">{error}</div> : null}
      {content ? (
        <>
          <div className="ai-headline">{content.headline}</div>
          <p className="ai-text">{content.summary}</p>
          <div className="ai-columns">
            <List title="Highlights" items={content.highlights} tone="good" />
            <List title="Concerns" items={content.concerns} tone="bad" />
            <List title="Next actions" items={content.actions} tone="act" />
          </div>
          <Footer record={record!} />
        </>
      ) : !busy ? (
        <p className="small muted" style={{ margin: 0 }}>
          Reviews calls, appointments, pipeline, speed to lead and lead quality for this period against the one before,
          and suggests what to do next.
        </p>
      ) : null}
    </section>
  );
}
